import { getAuthChallenge, getClient, updateAuthChallenge, updateAccessToken, updateRefreshToken, useTranslate } from 'utils'
import { StaticPageError } from 'components/styled'
import { redirect, useLoaderData, type LoaderFunctionArgs } from 'react-router-dom'

type LoginLoaderData = { error: string } | null

export const loginLoader = async ({ request }: LoaderFunctionArgs): Promise<LoginLoaderData> => {
    const url = new URL(request.url)
    const code = url.searchParams.get('code')
    const to = url.searchParams.get('to')
    const error = url.searchParams.get('error')
    const errorDescription = url.searchParams.get('error_description')

    if (error) {
        return { error: errorDescription ?? error }
    }

    // fix: validate `to` is same-origin to prevent open redirect
    if (to && (to.startsWith('http') || to.startsWith('//'))) {
        const toUrl = new URL(to, url.origin)
        if (toUrl.origin !== url.origin) return { error: 'Invalid redirect' }
    }

    const safeTo = to || '/'

    if (!code) {
        const redirectURI = `${url.origin}${url.pathname}?to=${safeTo}`
        const { challenge, url: authUrl } = await getClient().authorize(redirectURI, 'code', { pkce: true })
        updateAuthChallenge(JSON.stringify(challenge))
        throw redirect(authUrl)
    }

    const redirectURI = `${url.origin}${url.pathname}?to=${safeTo}`
    const authChallengeRaw = getAuthChallenge()
    const authChallenge = authChallengeRaw ? JSON.parse(authChallengeRaw) : null
    if (authChallenge) {
        updateAuthChallenge(null)
    }

    const exchanged = await getClient().exchange(
        code,
        redirectURI,
        authChallenge?.verifier ?? ''
    )

    if (exchanged.err) {
        return { error: 'Token exchange failed' }
    }

    updateAccessToken(exchanged.tokens.access)
    updateRefreshToken(exchanged.tokens.refresh)
    throw redirect(safeTo)
}

const authErrorMessages: Record<string, string> = {
    user_not_activated: 'your_account_has_not_been_activated_yet_you_can_activate_your_account_by_opening_the_activation_link_that_sent_to_your_email_before',
    user_not_found: 'we_cant_seem_to_find_the_right_email_for_you_are_you_sure_you_have_an_account'
}

// Auth-service error_description values safe to display verbatim. error_description comes
// from the URL and is attacker-controllable, so any value outside this set is replaced with
// a generic message instead of rendered. Dynamic auth errors ("Unsupported response_type: <x>",
// "Client <id> ... redirect_uri: <uri>") are intentionally excluded — their variable parts are
// attacker-controlled.
const safeAuthErrors = new Set([
    ...Object.keys(authErrorMessages),
    'authorization_failed',
    'Invalid provider',
    'Missing parameter: response_type',
    'Missing parameter: client_id'
])

export const Login = () => {
    const { __ } = useTranslate()
    const data = useLoaderData() as LoginLoaderData
    if (data?.error) {
        const message = authErrorMessages[data.error]
            ? __(authErrorMessages[data.error])
            : safeAuthErrors.has(data.error)
                ? data.error
                : __`oops_something_went_wrong`
        return <StaticPageError message={message}/>
    }
    return <></>
}
